import {
  Component,
  forwardRef,
  OnDestroy,
  OnInit,
  HostListener
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormControl} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {Subscription, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {GeoService} from '../../../creator/services/geo/geo.service';

@Component({
  selector: 'app-city-selector',
  standalone: true,
  imports: [ReactiveFormsModule, TranslocoPipe],
  templateUrl: './city-selector.html',
  styleUrl: './city-selector.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CitySelector),
      multi: true
    }
  ]
})
export class CitySelector implements OnInit, OnDestroy, ControlValueAccessor {
  // UI state
  loadingCities = false;
  filteredCities: string[] = [];
  citiesSuggestionsShown = false;

  // Raw input control
  inputCtrl = new FormControl<string>('', { nonNullable: true });

  // Ensure selected value is a suggestion
  selectedCity: string | null = null;

  // Internal
  private search$ = new Subject<string>();
  private subs: Subscription[] = [];

  // CVA callbacks
  private onChange: (val: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly geoService: GeoService) {}

  ngOnInit(): void {
    // When typing, invalidate selection and push to search
    this.subs.push(
      this.inputCtrl.valueChanges.subscribe(value => {
        this.selectedCity = null;
        this.onChange(null); // not valid until selected from list
        this.search$.next(value);
      })
    );

    // Debounced search
    this.subs.push(
      this.search$.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(q => this.filterCities(q))
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ---- CVA ----
  writeValue(value: string | null): void {
    // Reflect initial value or patchValue from parent
    if (value) {
      this.inputCtrl.setValue(value, { emitEvent: false });
      this.selectedCity = value;
    } else {
      this.inputCtrl.setValue('', { emitEvent: false });
      this.selectedCity = null;
    }
  }
  registerOnChange(fn: (val: string | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.inputCtrl.disable({ emitEvent: false }) : this.inputCtrl.enable({ emitEvent: false });
  }

  // ---- Search + selection ----
  private filterCities(query: string): void {
    if (!query || query.trim().length < 2) {
      this.filteredCities = [];
      this.citiesSuggestionsShown = false;
      this.loadingCities = false;
      return;
    }
    this.loadingCities = true;
    this.geoService.searchCities(query).subscribe({
      next: (results: string[]) => {
        this.filteredCities = results ?? [];
        // Keep dropdown visible to show empty state if needed
        this.citiesSuggestionsShown = true;
        this.loadingCities = false;
      },
      error: () => {
        this.filteredCities = [];
        this.citiesSuggestionsShown = false;
        this.loadingCities = false;
      }
    });
  }

  selectCity(city: string): void {
    this.inputCtrl.setValue(city, { emitEvent: false });
    this.selectedCity = city;
    this.filteredCities = [];
    this.citiesSuggestionsShown = false;
    this.onChange(city);
    this.onTouched();
  }

  // Close on outside click
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('app-city-selector')) {
      this.citiesSuggestionsShown = false;
    }
  }

  handleEnter(): void {
    if (this.filteredCities.length > 0) this.selectCity(this.filteredCities[0]);
  }
  handleFocusIn(): void {
    this.citiesSuggestionsShown = this.filteredCities.length > 0 || this.loadingCities;
  }
  handleBlur(): void { this.onTouched(); }
}
